import type { CalibrationProfile, UpgradeId } from "../types";
import { DEFAULT_CONTROL_SETTINGS } from "../controlDefaults";
import { publicUrl } from "../publicUrl";

export type BalloonSpec = {
  id: number;
  spawnAt: number;
  duration: number;
  arc: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  color: string;
  scale: number;
  emergeAt: number;
  initialScale: number;
  finalScale: number;
  emergeY: number;
  launchZ: number;
};

export type Vec3 = { x: number; y: number; z: number };
export type ViewOffset = { x: number; y: number };
export type PortalViewport = CalibrationProfile["viewport"];

export type BalloonDynamics = CalibrationProfile["balloon"];

export function upgradeTier(upgrades: UpgradeId[], id: UpgradeId) {
  return upgrades.reduce((tier, upgrade) => tier + Number(upgrade === id), 0);
}

export function activeLaserDurationMs(
  laser: CalibrationProfile["laser"],
  prismTier = 0,
  coolingTier = 0,
) {
  const prismMultiplier = prismTier > 0 ? 2 + (prismTier - 1) * 0.14 : 1;
  const coolingMultiplier = 1 + coolingTier * 0.12;
  return laser.durationMs * prismMultiplier * coolingMultiplier;
}

export function laserRayOffsets(prismTier = 0) {
  if (prismTier <= 0) return [0];
  const rayCount = Math.min(7, 1 + prismTier * 2);
  const center = (rayCount - 1) / 2;
  return Array.from({ length: rayCount }, (_, index) => (index - center) * 0.075);
}

export function balloonLaserHitRadius(renderedRadius: number, lensTier = 0) {
  return renderedRadius * 1.18 + 0.025 + lensTier * 0.12;
}

export const UPGRADE_DELIVERY_WINDOW_SECONDS = 10;
export const UPGRADE_DRONE_HIT_RADIUS = 1.55;

export function upgradeDronePosition(id: UpgradeId, elapsed: number, showAt: number): Vec3 {
  const progress = Math.max(0, Math.min(1, (elapsed - showAt) / UPGRADE_DELIVERY_WINDOW_SECONDS));
  const direction = id === "cooling" ? -1 : 1;
  const phase = id === "lens" ? 0 : id === "cooling" ? Math.PI * 0.72 : Math.PI * 1.31;
  const crossing = direction * (-0.58 + progress * 1.16);
  return {
    x: crossing + Math.sin(progress * Math.PI * 2 + phase) * 0.1,
    y: 0.08 + Math.sin(progress * Math.PI * 2.25 + phase) * 0.12 + Math.sin(progress * Math.PI) * 0.12,
    z: -4.5 + Math.cos(progress * Math.PI * 2 + phase) * 0.45,
  };
}

const COLORS = ["#ff4f64", "#42c7d6", "#ffc83d", "#9b67e8", "#55b968"];
export const FENCE_DEPTH_Z = -18;
export const IMPACT_DEPTH_Z = 6.5;
export const CAMPAIGN_LEVEL_COUNT = 10;
export const CAMPAIGN_TOTAL_BALLOONS = 275;

export type CampaignLevelDefinition = {
  level: number;
  name: string;
  balloonCount: number;
  difficulty: number;
  intervalSeconds: number;
  flightSeconds: number;
  targetScatter: number;
  arcMultiplier: number;
  burstEvery: number | null;
  upgradeCount: number;
  music: string;
};

export type UpgradeDeliverySpec = {
  key: string;
  id: UpgradeId;
  afterBalloon: number;
};

const MUSIC_BY_LEVEL = [
  publicUrl("audio/backyard-reactor-level-1.mp3"),
  publicUrl("audio/backyard-reactor-level-1.mp3"),
  publicUrl("audio/backyard-reactor-level-1.mp3"),
  publicUrl("audio/backyard-reactor-level-2.mp3"),
  publicUrl("audio/backyard-reactor-level-2.mp3"),
  publicUrl("audio/backyard-gizmo-run-level-1.mp3"),
  publicUrl("audio/backyard-gizmo-run-level-1.mp3"),
  publicUrl("audio/backyard-gizmo-run-level-1.mp3"),
  publicUrl("audio/backyard-gizmo-run-level-2.mp3"),
  publicUrl("audio/backyard-gizmo-run-level-2.mp3"),
];

const LEVEL_NAMES = [
  "Field Test",
  "Fence Line",
  "Crosswind",
  "Double Volley",
  "Splash Surge",
  "Gizmo Rush",
  "Pressure Test",
  "Saturation",
  "Last Stand",
  "Backyard Boss",
];

function clampCampaignLevel(level: number) {
  return Math.max(1, Math.min(CAMPAIGN_LEVEL_COUNT, Math.round(level)));
}

function lerp(start: number, end: number, amount: number) {
  return start + (end - start) * amount;
}

export function campaignLevelDefinition(level: number): CampaignLevelDefinition {
  const safeLevel = clampCampaignLevel(level);
  const difficulty = (safeLevel - 1) / (CAMPAIGN_LEVEL_COUNT - 1);
  return {
    level: safeLevel,
    name: LEVEL_NAMES[safeLevel - 1],
    balloonCount: safeLevel * 5,
    difficulty,
    intervalSeconds: lerp(3.1, 1.3, difficulty),
    flightSeconds: lerp(4.95, 3.15, difficulty),
    targetScatter: lerp(0.72, 1, difficulty),
    arcMultiplier: lerp(0.94, 1.14, difficulty),
    burstEvery: safeLevel >= 5 ? Math.max(4, 9 - Math.floor((safeLevel - 5) / 2)) : null,
    upgradeCount: safeLevel <= 3 ? 1 : safeLevel <= 6 ? 2 : 3,
    music: MUSIC_BY_LEVEL[safeLevel - 1],
  };
}

export function upgradeScheduleForLevel(level: number): UpgradeDeliverySpec[] {
  const definition = campaignLevelDefinition(level);
  const upgradeIds: UpgradeId[] = ["lens", "cooling", "prism"];
  return Array.from({ length: definition.upgradeCount }, (_, index) => {
    const ideal = Math.floor(definition.balloonCount * (index + 1) / (definition.upgradeCount + 1)) - 2;
    return {
      key: `level-${definition.level}-delivery-${index + 1}`,
      id: upgradeIds[(definition.level + index - 1) % upgradeIds.length],
      afterBalloon: Math.max(1, Math.min(definition.balloonCount - 1, ideal)),
    };
  });
}

export function physicalEmergenceProgress(dynamics: BalloonDynamics) {
  const yard = Math.max(20, dynamics.yardDepthFt);
  const behindFence = Math.max(1, dynamics.throwerDistanceFt);
  return behindFence / (yard + behindFence);
}

function radicalInverse(index: number, base: number) {
  let value = 0;
  let fraction = 1 / base;
  while (index > 0) {
    value += (index % base) * fraction;
    index = Math.floor(index / base);
    fraction /= base;
  }
  return value;
}

export function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

export function createCampaignLevel(
  levelNumber: number,
  seed = 4242,
  viewport: PortalViewport = DEFAULT_CONTROL_SETTINGS.viewport,
  dynamics: BalloonDynamics = DEFAULT_CONTROL_SETTINGS.balloon,
): BalloonSpec[] {
  const definition = campaignLevelDefinition(levelNumber);
  const random = seededRandom(seed);
  const balloons: BalloonSpec[] = [];
  // The first balloon clears the fence shortly after the countdown so the
  // real game and the live sandbox have the same immediate feedback.
  let clock = 0.25;

  for (let index = 0; index < definition.balloonCount; index += 1) {
    const desiredHeadX = (radicalInverse(index + 1, 2) * 2 - 1) * definition.targetScatter * dynamics.destinationScatter.x;
    const desiredHeadY = (radicalInverse(index + 1, 3) * 2 - 1) * definition.targetScatter * 0.9 * dynamics.destinationScatter.y;
    const impactParallax = 1 + viewport.depthEffect;
    const targetX = viewport.framing.x + desiredHeadX * viewport.pan.x * 2 * impactParallax / viewport.zoom;
    const targetY = viewport.framing.y + desiredHeadY * viewport.pan.y * 2 * impactParallax / viewport.zoom;
    const arc = (0.15 + random() * 0.1) * definition.arcMultiplier * dynamics.arc * Math.sqrt(dynamics.fenceHeightFt / 8);
    const emergeAt = physicalEmergenceProgress(dynamics);
    const worldUnitsPerFoot = (IMPACT_DEPTH_Z - FENCE_DEPTH_Z) / Math.max(20, dynamics.yardDepthFt);
    balloons.push({
      id: index,
      spawnAt: clock,
      duration: (definition.flightSeconds + (random() - 0.5) * 0.35) * dynamics.flightTime,
      arc,
      startX: (random() * 2 - 1) * dynamics.originScatter,
      startY: dynamics.fenceBottom,
      targetX,
      targetY,
      color: COLORS[index % COLORS.length],
      scale: 0.72 + random() * 0.16,
      emergeAt,
      initialScale: dynamics.initialScale,
      finalScale: dynamics.finalScale,
      // The balloon becomes visible with its center only just above the fence.
      // Arc energy is applied to the visible parabola instead of teleporting
      // the emergence point upward into the sky.
      emergeY: dynamics.fenceTop + 0.025,
      launchZ: FENCE_DEPTH_Z - dynamics.throwerDistanceFt * worldUnitsPerFoot,
    });
    const burst = definition.burstEvery && index > 0 && index % definition.burstEvery === 0 ? 0.55 : 1;
    clock += (definition.intervalSeconds * burst + (random() - 0.5) * 0.28) * dynamics.cadence;
  }

  return balloons;
}

export function createLevelOne(
  seed = 4242,
  viewport: PortalViewport = DEFAULT_CONTROL_SETTINGS.viewport,
  dynamics: BalloonDynamics = DEFAULT_CONTROL_SETTINGS.balloon,
) {
  return createCampaignLevel(1, seed, viewport, dynamics);
}

export function balloonPosition(spec: BalloonSpec, elapsed: number): Vec3 {
  const progress = Math.max(0, Math.min(1, (elapsed - spec.spawnAt) / spec.duration));
  const emergeAt = balloonEmergenceAt(spec);
  const visibleProgress = Math.max(0, Math.min(1, (progress - emergeAt) / Math.max(0.01, 1 - emergeAt)));
  const visibleY = spec.emergeY + (spec.targetY - spec.emergeY) * visibleProgress +
    4 * spec.arc * visibleProgress * (1 - visibleProgress);
  // The hidden section is a C1 Hermite continuation into the visible
  // constant-gravity parabola. It begins at the registered ground line, meets
  // the fence without a direction change, and remains fully occluded.
  const hiddenProgress = Math.max(0, Math.min(1, progress / Math.max(0.01, emergeAt)));
  const visibleSlopeAtFence = (spec.targetY - spec.emergeY + 4 * spec.arc) / Math.max(0.01, 1 - emergeAt);
  const launchSlope = (spec.emergeY - spec.startY) / Math.max(0.01, emergeAt) * 0.72;
  const h00 = 2 * hiddenProgress ** 3 - 3 * hiddenProgress ** 2 + 1;
  const h10 = hiddenProgress ** 3 - 2 * hiddenProgress ** 2 + hiddenProgress;
  const h01 = -2 * hiddenProgress ** 3 + 3 * hiddenProgress ** 2;
  const h11 = hiddenProgress ** 3 - hiddenProgress ** 2;
  const hiddenY = h00 * spec.startY + h10 * emergeAt * launchSlope +
    h01 * spec.emergeY + h11 * emergeAt * visibleSlopeAtFence;
  return {
    x: spec.startX + (spec.targetX - spec.startX) * progress,
    y: progress < emergeAt ? hiddenY : visibleY,
    z: spec.launchZ + (IMPACT_DEPTH_Z - spec.launchZ) * progress,
  };
}

export function balloonEmergenceAt(spec: BalloonSpec) {
  return Math.max(0.05, Math.min(0.35, spec.emergeAt));
}

export function balloonScaleAt(spec: BalloonSpec, elapsed: number) {
  const progress = Math.max(0, Math.min(1, (elapsed - spec.spawnAt) / spec.duration));
  const emergeAt = balloonEmergenceAt(spec);
  if (progress < emergeAt) return spec.initialScale;
  const visibleProgress = (progress - emergeAt) / Math.max(0.01, 1 - emergeAt);
  const eased = visibleProgress * visibleProgress * (3 - 2 * visibleProgress);
  return spec.initialScale + (spec.finalScale - spec.initialScale) * eased;
}

export function portalScreenPosition(position: Vec3, view: ViewOffset, viewport: PortalViewport): Vec3 {
  const depthProgress = Math.max(0, Math.min(1, (position.z - FENCE_DEPTH_Z) / (IMPACT_DEPTH_Z - FENCE_DEPTH_Z)));
  const parallax = 1 + viewport.depthEffect * depthProgress ** 1.4;
  return {
    x: (position.x - viewport.framing.x) * viewport.zoom - view.x * viewport.pan.x * 2 * parallax,
    y: (position.y - viewport.framing.y) * viewport.zoom - view.y * viewport.pan.y * 2 * parallax,
    z: position.z,
  };
}

export function photoLineScreenPercent(lineY: number, viewY: number, viewport: PortalViewport) {
  return 50 + (viewport.framing.y - lineY) * 50 * viewport.zoom + viewY * viewport.pan.y * 100;
}

export function playablePortalPosition(position: Vec3): Vec3 {
  // Targets are generated inside the webcam-mapped reachable envelope. Do not
  // clamp every in-flight sample: a hard per-frame clamp flattens the top and
  // sides of the parabola and makes a constant-velocity throw look steered.
  return position;
}

export function scoreForPop(combo: number) {
  return 100 * Math.max(1, Math.min(combo, 5));
}
